
import { useTranslation } from "react-i18next";
import { Pills, Tooltip } from "barte-design-system";
import { formatPercentage } from '@shared/helpers'
import { InputNumber } from "@/modules/shared/components";
import { TrashIcon } from "@phosphor-icons/react";

import styles from "./card-network-rates-table.module.css";

import type { CardNetworkRatesTableProps } from "./card-network-rates-table.type";


export const CardNetworkRatesTable = ({ data, isEditing, isInactive, onChange, onRemove, networkKey, newtworkRates, showErrors, taxAggregateVersion }: CardNetworkRatesTableProps) => {
    const { t } = useTranslation();

    const networksTranslate: Record<string, string> = {
        default: t('payfac.pageRatePlanModalNetworkDefault'),
        mastercard: t('payfac.pageRatePlanModalNetworkMastercard'),
        visa: t('payfac.pageRatePlanModalNetworkVisa'),
        elo: t('payfac.pageRatePlanModalNetworkElo'),
        hipercard: t('payfac.pageRatePlanModalNetworkHipercard'),
        american_express: t('payfac.pageRatePlanModalNetworkAmericanExpress'),
        americanExpress: t('payfac.pageRatePlanModalNetworkAmericanExpress'),
        discover: t('payfac.pageRatePlanModalNetworkDiscover'),
    }

    const calculateMarkup = (rate?: number | string | null, networkRate?: number | string | null) => {
        if (rate === null || rate === undefined || rate === '') return null;
        const validRate = Number(rate);
        const validNetworkRate = Number(networkRate) || 0;

        return validRate - validNetworkRate;
    };

    const handleRateChange = (val: string | number | null, index: number, networkRate?: number | null) => {
        if (val === null || val === undefined || val === '' || `${val}` === 'NaN') {
            onChange?.(index, +val);
            return;
        }

        if (taxAggregateVersion === "1") {
            onChange?.(index, Number(val));
        } else {
            const newTotal = +(val || 0) + (+(networkRate) || 0);
            onChange?.(index, +(newTotal));
        }
    };

    const handleTotalChange = (val: string | number | null, index: number, networkRate?: number | null) => {
        if (val === null || val === undefined || val === '' || `${val}` === 'NaN') {
            onChange?.(index, +(val));
            return;
        }

        if (taxAggregateVersion === "1") {
            onChange?.(index, Number(val) - (+(networkRate) || 0));
        } else {
            onChange?.(index, Number(val));
        }
    };

    const getValueFirstField = (rate: number | string | null | undefined, networkRate?: number | null) => {
        if (taxAggregateVersion === "1") {
            if (rate === null || rate === undefined || rate === '') return null;
            return +rate;
        }

        return calculateMarkup(rate, networkRate);
    };

    const getValueLastField = (rate: number | string | null | undefined, networkRate?: number | null) => {
        if (taxAggregateVersion === "1") {
            if (rate === null || rate === undefined || rate === '') return null;

            return +(rate) + (+(networkRate) || 0);
        }

        if (rate === null || rate === undefined || rate === '') return null;
        return +rate;
    };

    const hasError = (displayedValue: number | null, rate?: number | string | null) => {
        if ((displayedValue ?? 0) < 0) return true;

        if (!showErrors) return false;

        if (rate === null || rate === undefined || rate === '' || `${rate}` === 'NaN') return true;

        return false;
    };

    return (
        <div className={styles.container}>
            <div className={styles['network-title']}>
                <span className={styles['network-name']}>
                    {networkKey ? networksTranslate[networkKey] : ''}

                    {isInactive && networkKey !== 'default' && (
                        <Tooltip label={t('payfac.pageRatePlanModalDefaultRateTooltip')}>
                            <Pills
                                label={t('payfac.pageRatePlanModalDefaultRateBadge')}
                                state="warning"
                            />
                        </Tooltip>
                    )}
                </span>

                {isEditing && networkKey !== 'default' && !isInactive && (
                    <TrashIcon
                        size={16}
                        className={styles['remove-icon']}
                        onClick={() => onRemove?.(networkKey as string)}
                    />
                )}
            </div>

            <ul className={styles.list}>
                <li className={styles['item-header']}>
                    <div className={styles['item-column-header']}>{t('payfac.pageRatePlanModalTableColumnMarkup')}</div>

                    <div className={styles['item-column-header']}>{t('payfac.pageRatePlanModalTableColumnBarte')}</div>

                    <div className={styles['item-column-header']}>{t('payfac.pageRatePlanModalTableColumnTotal')}</div>
                </li>

                {data?.sellerRates?.map((rate, index) => {
                    return (
                        <li key={index} className={styles.item}>
                            <div className={styles['item-column']}>
                                {isEditing ? (
                                    <InputNumber
                                        value={getValueFirstField(rate, newtworkRates?.[index]) as any}
                                        onChange={(val) => handleRateChange(val, index, newtworkRates?.[index])}
                                        decimalPrecision={2}
                                        className={styles['input-number']}
                                        error={hasError(getValueFirstField(rate, newtworkRates?.[index]), rate)}
                                        suffix="%"
                                    />
                                ) : (
                                    formatPercentage(getValueFirstField(rate, newtworkRates?.[index]))
                                )}
                            </div>

                            <div className={styles['item-column']}>{formatPercentage(newtworkRates?.[index] || 0)}</div>

                            <div className={styles['item-column']}>
                                {isEditing ? (
                                    <InputNumber
                                        value={getValueLastField(rate, newtworkRates?.[index]) as any}
                                        onChange={(val) => handleTotalChange(val, index, newtworkRates?.[index])}
                                        decimalPrecision={2}
                                        className={styles['input-number']}
                                        error={hasError(getValueLastField(rate, newtworkRates?.[index]), rate)}
                                        suffix="%"
                                    />
                                ) : (
                                    formatPercentage(getValueLastField(rate, newtworkRates?.[index]))
                                )}
                            </div>
                        </li>
                    )
                })}
            </ul>
        </div>
    );
};